import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../Constants';

// Integrações via MCP (piloto Slack, leitura e escrita). Espelha
// ERPConnectionsContext: lista, conecta (OAuth em popup) e desconecta.
// `enabled` vem de /api/info/features COM o token do usuário: a flag do
// servidor só vale para quem está na allowlist, e o FeatureFlagsProvider
// (fora do Auth0Provider) busca sem autenticação.

export interface IntegrationConnection {
  connection_id: string;
  provider: string;
  status: string;
  workspace_id?: string;
  workspace_name?: string;
  account_label?: string;
  scopes: string[];
  tools: string[];
  write_tools?: string[];
  connected_at?: string;
  last_used_at?: string;
  last_error?: string;
}

interface IntegrationsContextValue {
  enabled: boolean;
  connections: IntegrationConnection[];
  loading: boolean;
  fetchConnections: () => Promise<void>;
  connectSlack: () => Promise<{ success: boolean; authorize_url?: string; message?: string }>;
  disconnect: (connectionId: string) => Promise<{ success: boolean; message: string }>;
}

const IntegrationsContext = createContext<IntegrationsContextValue | null>(null);

export function useIntegrations() {
  const ctx = useContext(IntegrationsContext);
  if (!ctx) {
    throw new Error('useIntegrations must be used within IntegrationsProvider');
  }
  return ctx;
}

export function IntegrationsProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [enabled, setEnabled] = useState(false);
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [loading, setLoading] = useState(false);

  const getHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Auth ${token}`,
      'Content-Type': 'application/json',
    };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) {
      setEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const resp = await fetch(`${API_BASE}/api/info/features`, { headers });
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) setEnabled(Boolean(data?.mcp_integrations));
        }
      } catch (err) {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, getHeaders]);

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/integrations`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setConnections(data.connections || []);
      }
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const connectSlack = useCallback(async () => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/integrations/slack/connect`, { method: 'POST', headers });
      const data = await resp.json();
      if (resp.ok) {
        return { success: true, authorize_url: data.authorize_url as string };
      }
      return { success: false, message: data.detail || 'Erro ao iniciar a conexão com o Slack.' };
    } catch (err) {
      return { success: false, message: 'Erro de rede ao conectar o Slack.' };
    }
  }, [getHeaders]);

  const disconnect = useCallback(async (connectionId: string) => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/integrations/${encodeURIComponent(connectionId)}`, {
        method: 'DELETE',
        headers,
      });
      const data = await resp.json();
      if (resp.ok) {
        await fetchConnections();
        return { success: true, message: data.message };
      }
      return { success: false, message: data.detail || 'Erro ao desconectar.' };
    } catch (err) {
      return { success: false, message: 'Erro de rede ao desconectar.' };
    }
  }, [getHeaders, fetchConnections]);

  return (
    <IntegrationsContext.Provider value={{
      enabled,
      connections,
      loading,
      fetchConnections,
      connectSlack,
      disconnect,
    }}>
      {children}
    </IntegrationsContext.Provider>
  );
}
